import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useApi } from "@/shared/utils/request";
import { getApiBaseUrl } from "@/shared/utils/apiUrl";
import { Button } from "@/shared/components/ui/button";
import { ExtLayout } from "./Layout";

declare const chrome: {
  runtime: {
    sendMessage: (extensionId: string, message: unknown) => Promise<{ success: boolean; error?: string }>;
  };
};

type Status =
  | { step: "connecting"; message: string }
  | { step: "success"; message: string }
  | { step: "error"; message: string };

export function ExtSignIn() {
  const [params] = useSearchParams();
  const extensionId = params.get("extension");
  const { makeApiRequest } = useApi();
  const [status, setStatus] = useState<Status>({
    step: "connecting",
    message: "Connecting to extension...",
  });
  const ran = useRef(false);

  const run = useCallback(async () => {
    if (!extensionId) return;

    // 1. Generate extension API key
    setStatus({ step: "connecting", message: "Generating secure key..." });

    const resp = await makeApiRequest({
      endpoint: "/e/auth/signin",
      method: "POST",
    });

    if (resp.error) {
      setStatus({ step: "error", message: resp.error });
      return;
    }

    const token = resp.data?.token as string;
    if (!token) {
      setStatus({ step: "error", message: "No token received from server." });
      return;
    }

    // 2. Send token to extension via chrome.runtime.sendMessage
    setStatus({ step: "connecting", message: "Signing in to extension..." });

    if (!chrome?.runtime?.sendMessage) {
      setStatus({
        step: "error",
        message: "Extension not found. Make sure the Hourglass extension is installed.",
      });
      return;
    }

    try {
      const appUrl = window.location.origin;
      const apiUrl = getApiBaseUrl();

      const response = await chrome.runtime.sendMessage(extensionId, {
        type: "auth:callback",
        payload: {
          url: { app: appUrl, api: apiUrl },
          token,
        },
      });

      if (response?.success) {
        setStatus({ step: "success", message: "Signed in! You can close this tab." });
        return;
      } else {
        const errorCode = response?.error ?? "unknown";
        const messages: Record<string, string> = {
          origin_rejected: "This site is not authorized to sign in to the extension.",
          invalid_payload: "Sign-in message was rejected by the extension.",
          auth_validation_failed: "The extension could not verify your credentials. Please try again.",
        };
        setStatus({
          step: "error",
          message: messages[errorCode] ?? `Extension rejected sign-in: ${errorCode}`,
        });
      }
    } catch {
      setStatus({
        step: "error",
        message: "Could not reach the extension. Make sure it is installed and enabled.",
      });
    }
  }, [extensionId, makeApiRequest]);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (extensionId) run();
  }, [extensionId, run]);

  // Missing extension ID — user navigated here manually
  if (!extensionId) {
    return (
      <ExtLayout>
        <h1 className="text-4xl font-bold tracking-tight">Something went wrong</h1>
        <p className="text-muted-foreground">
          Open this page from the Hourglass browser extension to sign in.
        </p>
      </ExtLayout>
    );
  }

  return (
    <ExtLayout>
      {status.step === "connecting" ? (
        <>
          <h1 className="text-4xl font-bold tracking-tight">Signing in...</h1>
          <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
        </>
      ) : status.step === "success" ? (
        <>
          <h1 className="text-4xl font-bold tracking-tight">Signed in</h1>
          <p className="text-sm text-muted-foreground">{status.message}</p>
        </>
      ) : (
        <>
          <h1 className="text-4xl font-bold tracking-tight">Something went wrong</h1>
          <p className="text-sm text-muted-foreground">{status.message}</p>
          <Button
            variant="outline"
            className="w-fit"
            onClick={() => {
              ran.current = false;
              run();
            }}
          >
            Try again
          </Button>
        </>
      )}
    </ExtLayout>
  );
}
